import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import {
    TelegramLogo,
    WhatsappLogo,
    Envelope,
    PaperPlaneTilt,
    Users,
    Check,
    X,
    Warning
} from "@phosphor-icons/react";

const CHANNELS = [
    { id: "telegram", label: "Telegram", Icon: TelegramLogo, color: "#0088CC", field: "telegram_chat_id" },
    { id: "whatsapp", label: "WhatsApp", Icon: WhatsappLogo, color: "#25D366", field: "whatsapp_id" },
    { id: "email", label: "Email", Icon: Envelope, color: "#EA4335", field: "email" },
];

const MessagingPage = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [channel, setChannel] = useState("telegram");
    const [subject, setSubject] = useState("");
    const [message, setMessage] = useState("");
    const [sendResults, setSendResults] = useState([]);

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            const response = await axios.get(`${API}/users/list`);
            setUsers(response.data);
        } catch (error) {
            toast.error("Error al cargar usuarios");
        } finally {
            setLoading(false);
        }
    };

    const toggleUser = (userId) => {
        setSelectedUsers(prev => 
            prev.includes(userId) 
                ? prev.filter(id => id !== userId)
                : [...prev, userId]
        );
    };

    const activeChannel = CHANNELS.find(c => c.id === channel) || CHANNELS[0];

    const selectAll = () => {
        const eligibleUsers = users.filter(u => u[activeChannel.field]);
        setSelectedUsers(eligibleUsers.map(u => u.id));
    };

    const selectNone = () => {
        setSelectedUsers([]);
    };

    const handleSend = async () => {
        if (selectedUsers.length === 0) {
            toast.error("Selecciona al menos un destinatario");
            return;
        }
        if (!message.trim()) {
            toast.error("Escribe un mensaje");
            return;
        }
        if (channel === "email" && !subject.trim()) {
            toast.error("El asunto es requerido para emails");
            return;
        }

        setSending(true);
        setSendResults([]);
        const results = [];

        for (const userId of selectedUsers) {
            const user = users.find(u => u.id === userId);
            if (!user) continue;

            const recipient = user[activeChannel.field];

            if (!recipient) {
                results.push({ user: user.name, success: false, error: `Sin ${activeChannel.label}` });
                continue;
            }

            try {
                await axios.post(`${API}/notifications/send`, {
                    type: channel,
                    recipient: recipient,
                    subject: subject,
                    message: message
                });
                results.push({ user: user.name, success: true });
            } catch (error) {
                results.push({ user: user.name, success: false, error: error.response?.data?.detail || "Error" });
            }
        }

        setSendResults(results);
        setSending(false);

        const successCount = results.filter(r => r.success).length;
        if (successCount === results.length) {
            toast.success(`Mensaje enviado a ${successCount} usuario(s)`);
        } else if (successCount > 0) {
            toast.warning(`Enviado a ${successCount} de ${results.length} usuarios`);
        } else {
            toast.error("No se pudo enviar a ningún usuario");
        }
    };

    const getEligibleCount = () => {
        return users.filter(u => u[activeChannel.field]).length;
    };

    if (loading) {
        return <div className="flex items-center justify-center h-64"><div className="spinner"></div></div>;
    }

    return (
        <div className="space-y-6 animate-in" data-testid="messaging-page">
            <div>
                <h1 className="font-heading font-bold text-2xl md:text-3xl">Mensajería</h1>
                <p className="text-[#555555] mt-1">Envía mensajes a usuarios por Telegram, WhatsApp o Email</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Left: User Selection */}
                <div className="lg:col-span-1 space-y-4">
                    <div className="bg-white border border-[#E5E5E5] rounded-sm">
                        <div className="p-4 border-b border-[#E5E5E5]">
                            <h2 className="font-heading font-bold flex items-center gap-2">
                                <Users size={20} />
                                Destinatarios
                            </h2>
                        </div>
                        <div className="p-4">
                            <div className="flex gap-2 mb-4">
                                <Button size="sm" variant="outline" onClick={selectAll}>
                                    Todos ({getEligibleCount()})
                                </Button>
                                <Button size="sm" variant="outline" onClick={selectNone}>
                                    Ninguno
                                </Button>
                            </div>
                            <div className="space-y-2 max-h-[400px] overflow-y-auto">
                                {users.map(user => {
                                    const canReceive = user[activeChannel.field];
                                    return (
                                        <label
                                            key={user.id}
                                            className={`flex items-center gap-3 p-3 rounded-sm border cursor-pointer transition-colors ${
                                                selectedUsers.includes(user.id)
                                                    ? "border-[#FFD700] bg-[#FFD700]/10"
                                                    : "border-[#E5E5E5] hover:border-[#0055FF]"
                                            } ${!canReceive ? "opacity-50" : ""}`}
                                        >
                                            <Checkbox
                                                checked={selectedUsers.includes(user.id)}
                                                onCheckedChange={() => toggleUser(user.id)}
                                                disabled={!canReceive}
                                            />
                                            <div className="flex-1 min-w-0">
                                                <p className="font-medium text-sm truncate">{user.name}</p>
                                                <p className="text-xs text-[#555555] truncate">
                                                    {channel === "email"
                                                        ? user.email
                                                        : (canReceive ? `${activeChannel.label} vinculado` : `Sin ${activeChannel.label}`)
                                                    }
                                                </p>
                                            </div>
                                            {canReceive ? (
                                                <Check size={16} className="text-green-500" />
                                            ) : (
                                                <X size={16} className="text-[#CCC]" />
                                            )}
                                        </label>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right: Message Composition */}
                <div className="lg:col-span-2 space-y-4">
                    <div className="bg-white border border-[#E5E5E5] rounded-sm p-6">
                        <h2 className="font-heading font-bold mb-4">Componer mensaje</h2>
                        
                        <div className="space-y-4">
                            <div>
                                <Label className="label-overline mb-2 block">Canal de envío</Label>
                                <div className="flex gap-4">
                                    {CHANNELS.map(({ id, label, Icon, color }) => (
                                        <button
                                            key={id}
                                            onClick={() => { setChannel(id); setSelectedUsers([]); }}
                                            className={`flex-1 flex items-center justify-center gap-2 p-4 rounded-sm border-2 transition-colors ${
                                                channel === id
                                                    ? "bg-opacity-10"
                                                    : "border-[#E5E5E5]"
                                            }`}
                                            style={channel === id ? { borderColor: color, backgroundColor: `${color}1A` } : undefined}
                                        >
                                            <Icon size={24} style={{ color }} weight="fill" />
                                            <span className="font-medium">{label}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {channel === "email" && (
                                <div>
                                    <Label className="label-overline mb-2 block">Asunto *</Label>
                                    <Input
                                        value={subject}
                                        onChange={(e) => setSubject(e.target.value)}
                                        className="input-industrial"
                                        placeholder="Asunto del email"
                                    />
                                </div>
                            )}

                            <div>
                                <Label className="label-overline mb-2 block">Mensaje *</Label>
                                <Textarea
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    className="input-industrial min-h-[150px]"
                                    placeholder="Escribe tu mensaje aquí..."
                                />
                            </div>

                            <div className="flex items-center justify-between pt-4">
                                <p className="text-sm text-[#555555]">
                                    {selectedUsers.length} destinatario(s) seleccionado(s)
                                </p>
                                <Button
                                    onClick={handleSend}
                                    disabled={sending || selectedUsers.length === 0}
                                    className="btn-primary"
                                >
                                    {sending ? (
                                        <span className="flex items-center gap-2">
                                            <div className="w-4 h-4 border-2 border-[#111] border-t-transparent rounded-full animate-spin"></div>
                                            Enviando...
                                        </span>
                                    ) : (
                                        <span className="flex items-center gap-2">
                                            <PaperPlaneTilt size={18} />
                                            Enviar mensaje
                                        </span>
                                    )}
                                </Button>
                            </div>
                        </div>
                    </div>

                    {/* Send Results */}
                    {sendResults.length > 0 && (
                        <div className="bg-white border border-[#E5E5E5] rounded-sm p-4">
                            <h3 className="font-semibold mb-3">Resultado del envío</h3>
                            <div className="space-y-2">
                                {sendResults.map((result, i) => (
                                    <div
                                        key={i}
                                        className={`flex items-center justify-between p-2 rounded-sm ${
                                            result.success ? "bg-green-50" : "bg-red-50"
                                        }`}
                                    >
                                        <span className="font-medium">{result.user}</span>
                                        {result.success ? (
                                            <span className="flex items-center gap-1 text-green-600 text-sm">
                                                <Check size={16} /> Enviado
                                            </span>
                                        ) : (
                                            <span className="flex items-center gap-1 text-red-600 text-sm">
                                                <Warning size={16} /> {result.error}
                                            </span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MessagingPage;
